import * as React from "react";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type Header,
  type RowData,
  type SortingState,
} from "@tanstack/react-table";
import { cva } from "class-variance-authority";
import { ArrowDown, ArrowUp, ArrowUpDown } from "./icons";

import { cn } from "utils/cn";
import { EmptyState } from "./EmptyState";
import { Skeleton } from "./Skeleton";

// Per-column presentation options, read from `columnDef.meta`:
//   { accessorKey: "amount", header: "Amount", meta: { align: "right" } }
declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    align?: "left" | "right" | "center";
    /** Extra classes for this column's header and body cells (widths, wrapping). */
    className?: string;
  }
}

type Align = "left" | "right" | "center";
type Density = "comfortable" | "compact";

const alignClass: Record<Align, string> = {
  left: "text-left",
  right: "text-right",
  center: "text-center",
};

const headerCellVariants = cva(
  "whitespace-nowrap px-4 align-middle text-xs font-medium text-muted-foreground",
  {
    variants: {
      density: { comfortable: "h-10", compact: "h-9" },
      // Sticky headers need an opaque fill or the scrolled rows show through.
      sticky: { true: "sticky top-0 z-10 bg-surface-muted", false: "" },
    },
    defaultVariants: { density: "comfortable", sticky: false },
  }
);

const rowVariants = cva("border-b border-border last:border-b-0", {
  variants: {
    density: { comfortable: "h-12", compact: "h-10" },
    interactive: {
      true: [
        "cursor-pointer outline-none transition-colors duration-fast ease-standard",
        "hover:bg-surface-muted focus-visible:bg-surface-muted",
        "focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring",
      ],
      false: "",
    },
  },
  defaultVariants: { density: "comfortable", interactive: false },
});

// Varied skeleton widths so the loading state reads as text rather than a grid of bars.
const skeletonWidths = ["w-3/4", "w-1/2", "w-2/3", "w-2/5", "w-3/5"];

// Clicks on controls inside a row (copy buttons, links, menus) must not also open the row.
const INTERACTIVE_SELECTOR =
  "a, button, input, select, textarea, label, summary, [role='button'], [role='menuitem'], [role='checkbox'], [role='switch']";

export interface DataTableProps<TData, TValue = unknown> {
  /** TanStack column definitions; `meta.align` / `meta.className` style a column's cells. */
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  /** Shows five skeleton rows in place of the body. */
  loading?: boolean;
  /** Shown when there are no rows. Defaults to a compact "No results" empty state. */
  empty?: React.ReactNode;
  /** Stable row ids (e.g. `(row) => row._id`); defaults to the row index. */
  getRowId?: (row: TData, index: number) => string;
  /** Makes rows focusable and clickable (Enter / Space too). */
  onRowClick?: (row: TData) => void;
  /** With `onRowClick`: rows this returns false for stay static (e.g. unconnected clients). */
  isRowClickable?: (row: TData) => boolean;
  /** Client-side sorting from the column headers. Set `enableSorting: false` on a column to opt out. */
  enableSorting?: boolean;
  initialSorting?: SortingState;
  /**
   * Keeps the header visible while the body scrolls. The table scrolls inside its own frame, so
   * give the table a height limit through `className` (e.g. `max-h-[28rem]`).
   */
  stickyHeader?: boolean;
  density?: Density;
  /** Rendered below the table inside the frame, usually <Pagination>. */
  footer?: React.ReactNode;
  /** Accessible name for the table (visually hidden). */
  caption?: string;
  className?: string;
}

const SortIcon: React.FC<{ direction: false | "asc" | "desc" }> = ({ direction }) => {
  if (direction === "asc") {
    return <ArrowUp aria-hidden="true" className="h-3.5 w-3.5 text-foreground" />;
  }
  if (direction === "desc") {
    return <ArrowDown aria-hidden="true" className="h-3.5 w-3.5 text-foreground" />;
  }
  return <ArrowUpDown aria-hidden="true" className="h-3.5 w-3.5 text-subtle-foreground" />;
};

const ariaSort = (direction: false | "asc" | "desc"): React.AriaAttributes["aria-sort"] => {
  if (direction === "asc") {
    return "ascending";
  }
  return direction === "desc" ? "descending" : "none";
};

function HeaderCell<TData>({
  header,
  density,
  sticky,
}: {
  header: Header<TData, unknown>;
  density: Density;
  sticky: boolean;
}) {
  const { column } = header;
  const meta = column.columnDef.meta;
  const align = meta?.align || "left";
  const canSort = column.getCanSort();
  const sorted = column.getIsSorted();
  const content = header.isPlaceholder
    ? null
    : flexRender(column.columnDef.header, header.getContext());

  return (
    <th
      scope="col"
      colSpan={header.colSpan}
      aria-sort={canSort ? ariaSort(sorted) : undefined}
      className={cn(headerCellVariants({ density, sticky }), alignClass[align], meta?.className)}
    >
      {canSort && !header.isPlaceholder ? (
        <button
          type="button"
          onClick={column.getToggleSortingHandler()}
          className={cn(
            "-mx-1.5 inline-flex h-7 items-center gap-1.5 rounded-control px-1.5 font-medium",
            "transition-colors duration-fast ease-standard hover:bg-surface-muted hover:text-foreground",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            sorted && "text-foreground",
            // Right-aligned (numeric) columns keep the label flush with the numbers below.
            align === "right" && "flex-row-reverse"
          )}
        >
          {content}
          <SortIcon direction={sorted} />
        </button>
      ) : (
        content
      )}
    </th>
  );
}

function DataTableInner<TData, TValue = unknown>(
  {
    columns,
    data,
    loading = false,
    empty,
    getRowId,
    onRowClick,
    isRowClickable,
    enableSorting = true,
    initialSorting,
    stickyHeader = false,
    density = "comfortable",
    footer,
    caption,
    className,
  }: DataTableProps<TData, TValue>,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  const [sorting, setSorting] = React.useState<SortingState>(initialSorting || []);

  const table = useReactTable<TData>({
    data,
    columns,
    getRowId,
    state: { sorting },
    onSortingChange: setSorting,
    enableSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const columnCount = Math.max(1, table.getVisibleLeafColumns().length);
  const rows = table.getRowModel().rows;
  const interactive = Boolean(onRowClick);

  const activate = (event: React.SyntheticEvent<HTMLTableRowElement>, original: TData) => {
    const target = event.target as HTMLElement;
    const control = target.closest(INTERACTIVE_SELECTOR);
    if (control && control !== event.currentTarget && event.currentTarget.contains(control)) {
      return;
    }
    // Dragging to select text in a cell is not a click on the row.
    if (event.type === "click" && window.getSelection()?.toString()) {
      return;
    }
    onRowClick?.(original);
  };

  let body: React.ReactNode;
  if (loading) {
    body = Array.from({ length: 5 }, (_, rowIndex) => (
      <tr key={`skeleton-${rowIndex}`} className={rowVariants({ density })}>
        {table.getVisibleLeafColumns().map((column, columnIndex) => {
          const align = column.columnDef.meta?.align || "left";
          return (
            <td key={column.id} className={cn("px-4", column.columnDef.meta?.className)}>
              <Skeleton
                className={cn(
                  "h-4",
                  skeletonWidths[(rowIndex + columnIndex) % skeletonWidths.length],
                  align === "right" && "ml-auto",
                  align === "center" && "mx-auto"
                )}
              />
            </td>
          );
        })}
      </tr>
    ));
  } else if (rows.length === 0) {
    body = (
      <tr>
        <td colSpan={columnCount}>{empty ?? <EmptyState compact title="No results" />}</td>
      </tr>
    );
  } else {
    body = rows.map((row) => {
      const clickable = interactive && (!isRowClickable || isRowClickable(row.original));
      return (
        <tr
          key={row.id}
          className={rowVariants({ density, interactive: clickable })}
          tabIndex={clickable ? 0 : undefined}
          onClick={clickable ? (event) => activate(event, row.original) : undefined}
          onKeyDown={
            clickable
              ? (event) => {
                  // Only the row itself: keys pressed on a button inside it stay with the button.
                  if (event.target !== event.currentTarget) {
                    return;
                  }
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    activate(event, row.original);
                  }
                }
              : undefined
          }
        >
          {row.getVisibleCells().map((cell) => {
            const meta = cell.column.columnDef.meta;
            return (
              <td
                key={cell.id}
                className={cn(
                  "px-4 align-middle",
                  alignClass[meta?.align || "left"],
                  meta?.className
                )}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            );
          })}
        </tr>
      );
    });
  }

  return (
    <div
      ref={ref}
      data-ui="data-table"
      className={cn(
        "flex flex-col overflow-hidden rounded-card border border-border bg-surface text-foreground shadow-card",
        className
      )}
    >
      <div className="min-h-0 flex-1 overflow-auto">
        <table
          className="w-full caption-bottom border-collapse text-sm"
          aria-busy={loading || undefined}
        >
          {caption && <caption className="sr-only">{caption}</caption>}
          <thead className="bg-surface-muted/60">
            {table.getHeaderGroups().map((group) => (
              <tr key={group.id} className="border-b border-border">
                {group.headers.map((header) => (
                  <HeaderCell
                    key={header.id}
                    header={header as Header<TData, unknown>}
                    density={density}
                    sticky={stickyHeader}
                  />
                ))}
              </tr>
            ))}
          </thead>
          <tbody>{body}</tbody>
        </table>
      </div>
      {footer && <div className="shrink-0 border-t border-border px-4 py-3">{footer}</div>}
    </div>
  );
}

const DataTableBase = React.forwardRef(DataTableInner);
DataTableBase.displayName = "DataTable";

/**
 * Sortable data table on TanStack Table. Columns are plain TanStack `ColumnDef`s; the table owns
 * sorting state, loading skeletons, the empty state and row activation.
 */
export const DataTable = DataTableBase as <TData, TValue = unknown>(
  props: DataTableProps<TData, TValue> & React.RefAttributes<HTMLDivElement>
) => React.ReactElement | null;
